import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Activity,
  Check,
  RefreshCw,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AuditService, AuditLog } from '@/services/AuditService';
import { useAuth } from '@/contexts/AuthContext';
import { LogDetailsModal } from './LogDetailsModal';
import { LogsStatsCards } from './LogsStatsCards';
import { LogsFilterPanel } from './LogsFilterPanel';
import { FixedPagination } from '@/components/ui/fixed-pagination';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

interface LogsFilters {
  search: string;
  action: string;
  user: string;
  dateFrom: string;
  dateTo: string;
}

export const LogsPage: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<LogsFilters>({
    search: '',
    action: 'all',
    user: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const fetchLogs = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const auditLogs = await AuditService.getRecentAuditLogs(1000);
      setLogs(auditLogs);
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Filtrar logs
  useEffect(() => {
    let filtered = [...logs];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.action.toLowerCase().includes(searchLower) ||
          (log.profiles?.name || '').toLowerCase().includes(searchLower) ||
          (log.profiles?.email || '').toLowerCase().includes(searchLower) ||
          JSON.stringify(log.details).toLowerCase().includes(searchLower)
      );
    }

    if (filters.action && filters.action !== 'all') {
      filtered = filtered.filter((log) => log.action === filters.action);
    }

    if (filters.user && filters.user !== 'all') {
      filtered = filtered.filter(
        (log) => (log.profiles?.name || 'Usuário do Sistema') === filters.user
      );
    }

    if (filters.dateFrom) {
      filtered = filtered.filter((log) => new Date(log.created_at) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      const endDate = new Date(filters.dateTo);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((log) => new Date(log.created_at) <= endDate);
    }

    setFilteredLogs(filtered);
    setCurrentPage(1); // Resetar para a primeira página ao filtrar
  }, [logs, filters]);

  const openLogDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };


  const getActionBadge = (action: string) => {
    const isCreate = action.includes('create');
    const isUpdate = action.includes('update');
    const isDelete = action.includes('delete');

    let icon = <Activity size={13} className="text-muted-foreground mr-1.5" />;
    
    if (isCreate) {
      icon = <Check size={12} className="text-emerald-500" />;
    } else if (isUpdate) {
      icon = <RefreshCw size={11} className="text-blue-500" />;
    } else if (isDelete) {
      icon = <X size={12} className="text-red-500" />;
    }

    return (
      <Badge 
        variant="outline" 
        className="gap-1.5 py-0.5 px-3 rounded-full border-muted-foreground/20 bg-muted/5 text-foreground font-medium text-[11px] whitespace-nowrap tracking-tight"
      >
        {icon}
        <span className="capitalize">{action.replace(/_/g, ' ')}</span>
      </Badge>
    );
  };

  const exportLogs = () => {
    const csvContent = [
      ['Data', 'Usuário', 'Ação', 'Detalhes'].join(','),
      ...filteredLogs.map((log) =>
        [
          format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss'),
          log.profiles?.name || 'Usuário do Sistema',
          log.action,
          `"${log.details.replace(/"/g, '""')}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const uniqueActions = useMemo(() => 
    Array.from(new Set(logs.map((log) => log.action))).sort(), 
  [logs]);

  const uniqueUsers = useMemo(() => 
    Array.from(new Set(logs.map((log) => log.profiles?.name || 'Usuário do Sistema'))).sort(),
  [logs]);

  // Paginação
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, currentPage, pageSize]);

  // Definição de Colunas da Tabela
  const columns: ColumnDef<AuditLog>[] = useMemo(() => [
    {
      accessorKey: 'created_at',
      header: 'Data e Hora',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {format(new Date(row.original.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true, locale: ptBR })}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'profiles.name',
      header: 'Usuário',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-blue-600 dark:text-blue-400">
            {row.original.profiles?.name || 'Usuário do Sistema'}
          </span>
          <span className="text-[10px] text-muted-foreground italic">
            {row.original.profiles?.email || 'sistema'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'action',
      header: 'Ação',
      cell: ({ row }) => (
        <div className="flex items-center">
          {getActionBadge(row.original.action)}
        </div>
      ),
    },
    {
      accessorKey: 'details',
      header: 'Resumo',
      cell: ({ row }) => {
        try {
          const details = JSON.parse(row.original.details);
          const summary = details.nome || details.entidade || Object.keys(details)[0] || 'Detalhes da ação';
          return (
            <div className="max-w-[300px] truncate text-xs text-muted-foreground">
              {String(summary).substring(0, 50)}
            </div>
          );
        } catch {
          return <div className="max-w-[300px] truncate text-xs text-muted-foreground">Ação realizada</div>;
        }
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-3 text-muted-foreground hover:text-foreground font-medium"
            onClick={() => openLogDetails(row.original)}
          >
            Detalhes
          </Button>
        </div>
      ),
    },
  ], []);

  const table = useReactTable({
    data: paginatedLogs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Activity className="h-8 w-8 animate-spin text-blue-500" />
        <span className="text-sm font-medium">Carregando logs de auditoria...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-6 pb-20">

      {/* Estatísticas */}
      <LogsStatsCards logs={logs} isLoading={loading} />

      {/* Painel de Filtros e Listagem */}
      <div className="flex flex-col flex-1 min-h-0 space-y-4">
        <LogsFilterPanel 
          filters={filters}
          onFilterChange={(newFilters) => setFilters(prev => ({ ...prev, ...newFilters }))}
          onClearFilters={() => setFilters({
            search: '',
            action: 'all',
            user: 'all',
            dateFrom: '',
            dateTo: '',
          })}
          uniqueActions={uniqueActions}
          uniqueUsers={uniqueUsers}
          totalLogs={logs.length}
          filteredCount={filteredLogs.length}
          onRefresh={fetchLogs}
          onExport={exportLogs}
        />

        {/* Tabela de Logs */}
        <div className="relative bg-card rounded-xl border border-border overflow-hidden shadow-sm">
          <style>{`
            [data-table-logs] thead {
              background: hsl(var(--muted) / 0.3);
            }
            [data-table-logs] thead tr {
              border-bottom: 1px solid hsl(var(--border)) !important;
            }
            [data-table-logs] th {
              font-size: 11px !important;
              font-weight: 600 !important;
              text-transform: uppercase !important;
              letter-spacing: 0.05em !important;
              color: hsl(var(--muted-foreground)) !important;
              padding: 12px 16px !important;
            }
            [data-table-logs] tbody tr {
              transition: background-color 0.15s ease;
            }
            [data-table-logs] tbody tr:hover {
              background: hsl(var(--muted) / 0.2);
            }
            [data-table-logs] td {
              padding: 14px 16px !important;
              font-size: 13px;
              color: hsl(var(--foreground));
              border-bottom: 1px solid hsl(var(--border) / 0.4);
            }
            [data-table-logs] tbody tr:last-child td {
              border-bottom: none;
            }
          `}</style>
          
          <div className="overflow-x-auto" data-table-logs>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
                        <Activity className="h-8 w-8 mb-2 opacity-20" />
                        <span className="text-sm font-medium">Nenhum log encontrado para os filtros atuais</span>
                        <span className="text-xs mt-1">Experimente ajustar sua busca ou limpar os filtros</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Paginação Fixa */}
      <FixedPagination
        currentPage={currentPage}
        totalPages={Math.ceil(filteredLogs.length / pageSize)}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setCurrentPage(1);
        }}
        pageSize={pageSize}
        totalItems={filteredLogs.length}
      />

      {/* Modal de Detalhes */}
      <LogDetailsModal
        log={selectedLog}
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      />
    </div>
  );
};
